import React, { useState } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  Row,
  Col,
  Typography,
  Space,
  message,
  Progress,
  Alert,
  Divider
} from 'antd';
import {
  LockOutlined,
  EyeInvisibleOutlined,
  EyeTwoTone,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SecurityScanOutlined
} from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import { changePassword } from '../../redux/slices/userSlices';
import { authService } from '../../services/auth.service';

const { Title, Text } = Typography;
const { Password } = Input;

const PasswordChangeForm = ({
  onSuccess,
  onCancel,
  showCancel = true,
  title = "Change Password",
  description = "Update your password to keep your account secure",
  isAdminReset = false,
  userId = null
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [passwordChecks, setPasswordChecks] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false
  });
  const dispatch = useDispatch();

  // Password strength calculation
  const calculatePasswordStrength = (password) => {
    if (!password) {
      setPasswordStrength(0);
      setPasswordChecks({
        length: false,
        uppercase: false,
        lowercase: false,
        number: false,
        special: false
      });
      return;
    }

    const checks = {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /[0-9]/.test(password),
      special: /[^A-Za-z0-9]/.test(password)
    };

    setPasswordChecks(checks);
    
    const score = Object.values(checks).filter(Boolean).length;
    setPasswordStrength((score / 5) * 100);
  };

  const getStrengthColor = (strength) => {
    if (strength < 40) return '#ff4d4f';
    if (strength < 70) return '#faad14';
    return '#52c41a';
  };

  const getStrengthText = (strength) => {
    if (strength < 40) return 'Weak';
    if (strength < 70) return 'Medium';
    return 'Strong';
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);

      // Validate password strength
      if (passwordStrength < 70) {
        message.error('Password is too weak. Please choose a stronger password.');
        return;
      }

      // Use Redux action for password change
      const result = await dispatch(changePassword({
        currentPassword: isAdminReset ? null : values.current_password,
        newPassword: values.new_password,
        confirmPassword: values.confirm_password,
        userId: isAdminReset ? userId : null
      })).unwrap();

      if (result.success) {
        message.success('Password changed successfully!');
        form.resetFields();
        setPasswordStrength(0);
        setPasswordChecks({
          length: false,
          uppercase: false,
          lowercase: false,
          number: false,
          special: false
        });
        
        if (onSuccess) {
          onSuccess(result);
        }
      }
    } catch (error) {
      console.error('Password change error:', error);
      message.error(error.message || 'Failed to change password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const PasswordStrengthIndicator = () => (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
        <Text type="secondary" style={{ fontSize: 12 }}>Password Strength</Text>
        <Text 
          style={{ 
            fontSize: 12, 
            color: getStrengthColor(passwordStrength),
            fontWeight: 'bold'
          }}
        >
          {getStrengthText(passwordStrength)}
        </Text>
      </div>
      <Progress
        percent={passwordStrength}
        strokeColor={getStrengthColor(passwordStrength)}
        showInfo={false}
        size="small"
      />
    </div>
  );

  const PasswordRequirements = () => (
    <div style={{ marginTop: 8 }}>
      <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
        Password Requirements:
      </Text>
      <Space direction="vertical" size={2}>
        <div style={{ display: 'flex', alignItems: 'center', fontSize: 12 }}>
          {passwordChecks.length ? (
            <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 4 }} />
          ) : (
            <CloseCircleOutlined style={{ color: '#ff4d4f', marginRight: 4 }} />
          )}
          <Text type={passwordChecks.length ? 'success' : 'secondary'} style={{ fontSize: 12 }}>
            At least 8 characters
          </Text>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', fontSize: 12 }}>
          {passwordChecks.uppercase ? (
            <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 4 }} />
          ) : (
            <CloseCircleOutlined style={{ color: '#ff4d4f', marginRight: 4 }} />
          )}
          <Text type={passwordChecks.uppercase ? 'success' : 'secondary'} style={{ fontSize: 12 }}>
            One uppercase letter
          </Text>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', fontSize: 12 }}>
          {passwordChecks.lowercase ? (
            <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 4 }} />
          ) : (
            <CloseCircleOutlined style={{ color: '#ff4d4f', marginRight: 4 }} />
          )}
          <Text type={passwordChecks.lowercase ? 'success' : 'secondary'} style={{ fontSize: 12 }}>
            One lowercase letter
          </Text>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', fontSize: 12 }}>
          {passwordChecks.number ? (
            <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 4 }} />
          ) : (
            <CloseCircleOutlined style={{ color: '#ff4d4f', marginRight: 4 }} />
          )}
          <Text type={passwordChecks.number ? 'success' : 'secondary'} style={{ fontSize: 12 }}>
            One number
          </Text>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', fontSize: 12 }}>
          {passwordChecks.special ? (
            <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 4 }} />
          ) : (
            <CloseCircleOutlined style={{ color: '#ff4d4f', marginRight: 4 }} />
          )}
          <Text type={passwordChecks.special ? 'success' : 'secondary'} style={{ fontSize: 12 }}>
            One special character
          </Text>
        </div>
      </Space>
    </div>
  );

  return (
    <Card
      style={{
        borderRadius: '12px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        border: 'none'
      }}
    >
      <div style={{ textAlign: 'center', marginBottom: 24 }}>
        <SecurityScanOutlined 
          style={{ 
            fontSize: 48, 
            color: '#1890ff', 
            marginBottom: 16 
          }} 
        />
        <Title level={3} style={{ margin: 0, color: '#1890ff' }}>
          {title}
        </Title>
        <Text type="secondary" style={{ fontSize: 14 }}>
          {description}
        </Text>
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        size="large"
        autoComplete="off"
      >
        {!isAdminReset && (
          <Form.Item
            label="Current Password"
            name="current_password"
            rules={[
              { required: true, message: 'Please enter your current password' }
            ]}
          >
            <Password
              placeholder="Enter your current password"
              prefix={<LockOutlined />}
              iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
            />
          </Form.Item>
        )}

        <Form.Item
          label="New Password"
          name="new_password"
          rules={[
            { required: true, message: 'Please enter a new password' },
            { min: 8, message: 'Password must be at least 8 characters' },
            {
              pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/,
              message: 'Password must contain uppercase, lowercase, number, and special character'
            }
          ]}
        >
          <Password
            placeholder="Enter your new password"
            prefix={<LockOutlined />}
            iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
            onChange={(e) => calculatePasswordStrength(e.target.value)}
          />
        </Form.Item>

        {passwordStrength > 0 && (
          <>
            <PasswordStrengthIndicator />
            <PasswordRequirements />
          </>
        )}

        <Form.Item
          label="Confirm New Password"
          name="confirm_password"
          dependencies={['new_password']}
          rules={[
            { required: true, message: 'Please confirm your new password' },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('new_password') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error('Passwords do not match'));
              },
            }),
          ]}
        >
          <Password
            placeholder="Confirm your new password"
            prefix={<LockOutlined />}
            iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
          />
        </Form.Item>

        <Divider />

        <Form.Item style={{ marginBottom: 0 }}>
          <Row gutter={16}>
            {showCancel && (
              <Col span={12}>
                <Button
                  block
                  size="large"
                  onClick={onCancel}
                  disabled={loading}
                >
                  Cancel
                </Button>
              </Col>
            )}
            <Col span={showCancel ? 12 : 24}>
              <Button
                type="primary"
                htmlType="submit"
                block
                size="large"
                loading={loading}
                disabled={passwordStrength < 70}
                icon={<SecurityScanOutlined />}
              >
                {loading ? 'Changing Password...' : 'Change Password'}
              </Button>
            </Col>
          </Row>
        </Form.Item>
      </Form>

      <Alert
        message="Security Tips"
        description="Choose a strong, unique password that you haven't used elsewhere. Consider using a password manager to generate and store secure passwords."
        type="info"
        showIcon
        style={{ marginTop: 16 }}
      />
    </Card>
  );
};

export default PasswordChangeForm;
